import { useRef } from "react";

//Common
import {
  DataGrid,
  Drawer,
  ConfirmationDialog,
  Snackbar,
  Chip,
} from "../../../common/VFX-UI";
import PageHeader from "../../../common/PageHeader/PageHeader";
import MoreActionsMenu from "../../../common/MoreActionsMenu/MoreActionsMenu";
import { MoreActionButton } from "../../../common/Buttons";
import SearchContainer from "../../../common/Forms/SearchContainer/SearchContainer";

//App
import LookupTypeFormContainer from "./LookupTypeFormContainer";
import LookupTypeView from "./LookupTypeView";
import useLookupType from "./hooks/useLookupType";
import { LookupTypeProps } from "../../../../types/lookupType";
import { SearchProps } from "../../../../types";
import { LookupTypeDto } from "../../../../app/model/api/types/dto";
import { SearchDefaultValue } from "../../../../store/types";
import { SearchFieldsDefaultValue } from "../../../../utility/constants";
import useComponentGeneralStyles from "../../../styles";
import DisplayData from "../../../common/DisplayData/DisplayData";

const LookupType: React.FC<LookupTypeProps> = (props) => {
  const { data: lookupTypes, deleteMutation, activateMutation } = props;
  const generalStyles = useComponentGeneralStyles();
  const dataGridRef = useRef<any>(null);

  const {
    state,
    formContext,
    rowEvents,
    actionColumnAnchorEl,
    toggleNewViewHandler,
    toggleViewHandler,
    toggleEditViewHandler,
    toggleDeactivateHandler,
    toggleActivateHandler,
    handleDeactivate,
    handleActivate,
    onShowSuccess,
    onAddSnackbarClose,
    handleActionClick,
    setActionColumnAnchorEl,
    handleActiveToggleChange,
  } = useLookupType({
    deleteMutation: deleteMutation,
    activateMutation: activateMutation,
  });

  const openMenu = Boolean(actionColumnAnchorEl);

  const columns = [
    {
      dataField: "id",
      text: "ID",
      hidden: true,
    },
    {
      dataField: "name",
      text: "Name",
      sort: true,
    },
    {
      dataField: "description",
      text: "Description",
      sort: true,
    },
    {
      dataField: "isGrouped",
      text: "Grouped",
      sort: false,
      formatter: (cell: boolean) => {
        return <DisplayData text="" importedData={cell ? "Yes" : "No"} />;
      },
    },
    {
      dataField: "isActive",
      text: "Status",
      sort: false,
      formatter: (cell: boolean) => {
        return <Chip label={cell ? "Active" : "Inactive"} />;
      },
    },
    {
      dataField: "action",
      text: "",
      style: { width: "25px" },
      isDummyField: true,
      formatter: () => {
        return (
          <MoreActionButton handleActionClick={(e) => handleActionClick(e)} />
        );
      },
    },
  ];

  const searchProps: SearchProps<LookupTypeDto> = {
    searchValue: formContext?.searchValue ?? SearchDefaultValue,
    searchFields: { ...SearchFieldsDefaultValue, name: true },
    setSearchValue: formContext?.setSearchValue,
    refetchGrid: formContext?.refetchGrid,
  };

  if (formContext?.paging?.currentPage === 1) {
    if (dataGridRef && dataGridRef.current) {
      dataGridRef.current.paginationContext.currPage = 1;
    }
  }

  return (
    <>
      <>
        <PageHeader>Lookup Type</PageHeader>
        <div style={generalStyles.searchContainer}>
          <SearchContainer
            searchProps={searchProps}
            activeToggle={formContext?.activeToggle ?? true}
            handleActiveToggleChange={handleActiveToggleChange}
            toggleNewViewHandler={toggleNewViewHandler}
          />
        </div>
      </>

      <Drawer show={state.toggleNewForm}>
        {state.toggleNewForm && (
          <LookupTypeFormContainer
            onClose={toggleNewViewHandler}
            onShowSuccess={onShowSuccess}
          />
        )}
      </Drawer>

      <Drawer show={state.toggleEditForm}>
        {state.toggleEditForm && formContext?.data && (
          <LookupTypeFormContainer
            onClose={toggleEditViewHandler}
            onShowSuccess={onShowSuccess}
          />
        )}
      </Drawer>

      <Drawer show={state.toggleView}>
        {state.toggleView && formContext?.data && (
          <LookupTypeView
            onClose={toggleViewHandler}
            editAction={() => {
              toggleEditViewHandler();
            }}
            toggleDeactivateHandler={toggleDeactivateHandler}
            toggleActivateHandler={toggleActivateHandler}
          />
        )}
      </Drawer>

      <DataGrid
        columns={columns}
        data={lookupTypes ?? []}
        keyField="id"
        rowEvents={rowEvents}
        totalSize={formContext?.paging?.totalSize}
        remote={true}
        pageSize={formContext?.pageSize}
        page={formContext?.paging?.currentPage}
        handlePageChange={async (newPage, pageSize, sortField, sortOrder) => {
          if (formContext?.setCurrentPage && newPage) {
            await formContext?.setCurrentPage(newPage);
          }
          if (formContext?.setCurrentPageSize && pageSize) {
            await formContext?.setCurrentPageSize(pageSize);
          }
          if (formContext?.setSortBy && sortField && sortOrder) {
            await formContext?.setSortBy({
              field: sortField,
              direction: sortOrder === "asc" ? 1 : -1,
            });
          }
          formContext?.refetchGrid();
        }}
      />

      {actionColumnAnchorEl && (
        <MoreActionsMenu
          isOpen={openMenu}
          target={actionColumnAnchorEl}
          toggle={() => {
            setActionColumnAnchorEl(null);
          }}
          onViewClick={() => {
            toggleViewHandler();
            setActionColumnAnchorEl(null);
          }}
        />
      )}

      <ConfirmationDialog
        open={state.isDeactivateDialogOpen}
        onClose={toggleDeactivateHandler}
        onConfirm={handleDeactivate}
        contentTitle={`Deactivate ${formContext?.data?.name}`}
        contentText={"Are you sure you want to continue?"}
        confirmText={"Yes, Continue"}
        closeText={"No, Go Back"}
      ></ConfirmationDialog>

      <ConfirmationDialog
        open={state.isActivateDialogOpen}
        onClose={toggleActivateHandler}
        onConfirm={handleActivate}
        contentTitle={`Activate ${formContext?.data?.name}`}
        contentText={"Are you sure you want to continue?"}
        confirmText={"Yes, Continue"}
        closeText={"No, Go Back"}
      />

      {state.showSuccessSnackbar && (
        <Snackbar
          open={state.showSuccessSnackbar}
          message={state.successMessage}
          handleClose={onAddSnackbarClose}
          severity="success"
        />
      )}
    </>
  );
};

export default LookupType;
